import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterProvider } from 'src/components/app/context/FilterContext';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import Accordion, {
  AccordionItem,
  AccordionItemHeader,
  AccordionItemPanel,
} from 'src/components/common/Accordion';
import CustomItemList from 'src/components/common/CustomItemList';
import OrgTree from 'src/components/common/OrgTree';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import type { PageFrameProps } from 'src/components/common/PageFrame';
import PageFrame from 'src/components/common/PageFrame';
import ReportOverview from 'src/components/report/OrgUnitReportsPage/components/ReportOverview';
import ShoppingCartBanner from 'src/components/report/OrgUnitReportsPage/components/ShoppingCartBanner';
import { ReportStatusContextProvider } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';
import { ShoppingCartProvider } from 'src/components/report/ShoppingCartContext';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

export const ORG_UNIT_CUSTOM_ORG_TREE_TEST_ID = 'org-unit-org-tree';
export const ORG_UNIT_CUSTOM_REPORT_TEST_ID = 'org-unit-custom-report';

export interface OrgUnitReportsPageProps extends PageFrameProps {}

export const OrgUnitReportsPage: FC<OrgUnitReportsPageProps> = () => {
  const { getCustomReports } = useWorkspaceContext();
  const { user } = useUserContext();
  const { t } = useTranslation('report');
  const { orgUnits } = useSection();
  const [firstOrgUnit] = orgUnits || [];
  const initialOrgUnits = orgUnits;
  const orgTreeState = useOrgTreeState();

  useEffect(() => {
    getCustomReports({ reset: true, sharedOnly: true });
  }, []);

  // Make sure there is a user,
  // initialOrgUnits when showing the orgTree or
  // firstOrgUnit when not showing the orgTree
  // before rendering.
  if (!user ? !initialOrgUnits : !firstOrgUnit) return null;

  return (
    <PageFrame
      sidebar={
        <Accordion initiallyActiveItems={['org-tree-accordion-item']}>
          <AccordionItem
            itemName="org-tree-accordion-item"
            aria-label={t('sidebar.titleGetReport')}
          >
            <AccordionItemHeader headline={t('sidebar.titleGetReport')} />

            <AccordionItemPanel data-test-id={ORG_UNIT_CUSTOM_ORG_TREE_TEST_ID}>
              <OrgTree orgTreeState={orgTreeState} itemsAreDraggable={false} />
            </AccordionItemPanel>
          </AccordionItem>
          <AccordionItem
            itemName="additional-reports--accordion-item"
            aria-label={t('sidebar.sharedReports')}
          >
            <AccordionItemHeader headline={t('sidebar.sharedReports')} />

            <AccordionItemPanel data-test-id={ORG_UNIT_CUSTOM_REPORT_TEST_ID}>
              <CustomItemList
                customItemType="sharedReports"
                infiniteScrolling
              />
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      }
      actionBanner={<ShoppingCartBanner />}
    >
      <ReportOverview orgUnit={orgTreeState.latestSelectedOrgUnit} />
    </PageFrame>
  );
};

// Wrap this page in a separate FilterProvider as its call of the useOrgTreeState-hook would run into racing conditions
// with the global initialization of the PlanActionContext and its call of the useOrgTreeState-hook.
const OrgUnitReportsPageWrapper: FC<OrgUnitReportsPageProps> = ({
  ...props
}) => (
  <FilterProvider>
    <ReportStatusContextProvider>
      <ShoppingCartProvider>
        <OrgUnitReportsPage {...props} />
      </ShoppingCartProvider>
    </ReportStatusContextProvider>
  </FilterProvider>
);

export default OrgUnitReportsPageWrapper;
